import React, { useRef } from 'react';
import { ExpandMore, UnfoldMore } from '@mui/icons-material';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { JournalByDate, Order } from '../Statistics.types';
import {
  arrowStyling,
  arrowStylingDefault,
  SortableHeaderButton,
} from '../../../components/TableHeaderButton';

interface JournalsTableDesktopProps {
  journals: JournalByDate[];
  onSort: (property: keyof JournalByDate) => void;
  order: Order;
  orderBy: keyof JournalByDate;
}

const columns: Array<{ key: keyof JournalByDate; label: string }> = [
  { key: 'count', label: 'statistics.table.amount' },
  { key: 'agreement', label: 'statistics.table.agreement' },
  { key: 'name', label: 'statistics.table.name' },
  { key: 'imprint', label: 'statistics.table.imprint' },
  { key: 'payment', label: 'statistics.table.payment' },
  { key: 'publishing_model', label: 'statistics.table.publishingModel' },
];

export const JournalsTableDesktop: React.FC<JournalsTableDesktopProps> = ({
  journals,
  onSort,
  order,
  orderBy,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);

  const getArrowIcon = (key: keyof JournalByDate) => {
    if (orderBy === key) {
      return order === 'asc' ? (
        <ExpandMore fontSize="small" style={arrowStyling} />
      ) : (
        <ExpandMore fontSize="small" style={arrowStylingDefault} />
      );
    }
    return <UnfoldMore fontSize="small" style={arrowStylingDefault} />;
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    property: keyof JournalByDate,
    columnIndex: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSort(property);
      return;
    }

    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    const nextIndex =
      event.key === 'ArrowRight'
        ? (columnIndex + 1) % columns.length
        : columnIndex === 0
          ? columns.length - 1
          : columnIndex - 1;

    const nextButton = tableRef.current?.querySelector(
      `th:nth-child(${nextIndex + 1}) button`
    ) as HTMLElement | null;
    nextButton?.focus();
  };

  return (
    <Box>
      <TableContainer sx={{ maxHeight: '60vh' }}>
        <Table
          stickyHeader
          size="small"
          ref={tableRef}
          aria-label={String(
            t('statistics.journals.tableLabel', 'Sortable journal statistics')
          )}
          aria-rowcount={journals.length + 1}
        >
          <caption className="sr-only">
            {t(
              'statistics.journals.tableCaption',
              `Journal click statistics showing ${journals.length} journals. Click column headers to sort.`
            )}
          </caption>
          <TableHead>
            <TableRow aria-rowindex={1}>
              {columns.map((column, columnIndex) => (
                <TableCell
                  key={String(column.key)}
                  sortDirection={orderBy === column.key ? order : false}
                  aria-sort={
                    orderBy === column.key
                      ? order === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  scope="col"
                >
                  <SortableHeaderButton
                    variant="secondary"
                    onClick={() => onSort(column.key)}
                    onKeyDown={(e) => handleKeyDown(e, column.key, columnIndex)}
                    isSorted={orderBy === column.key}
                    sortDirection={order}
                    sx={{ justifyContent: 'left' }}
                  >
                    {t(column.label)}
                    {getArrowIcon(column.key)}
                  </SortableHeaderButton>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {journals.map((row, index) => (
              <TableRow
                key={`${row.name}-${row.agreement}-${index}`}
                hover
                tabIndex={-1}
                aria-rowindex={index + 2}
              >
                <TableCell aria-label={`${row.count} clicks`}>
                  {row.count}
                </TableCell>
                <TableCell>
                  {row.agreement_id ? (
                    <Link
                      component="button"
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        if (e.ctrlKey || e.metaKey) {
                          window.open(
                            `/agreements/${row.agreement_id}`,
                            '_blank'
                          );
                        } else {
                          navigate(`/agreements/${row.agreement_id}`);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (e.ctrlKey || e.metaKey) {
                            window.open(
                              `/agreements/${row.agreement_id}`,
                              '_blank'
                            );
                          } else {
                            navigate(`/agreements/${row.agreement_id}`);
                          }
                        }
                      }}
                      tabIndex={0}
                    >
                      {row.agreement_name || row.agreement || ''}
                    </Link>
                  ) : (
                    row.agreement_name || row.agreement || ''
                  )}
                </TableCell>
                <TableCell>
                  {row.url ? (
                    <Link
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.name}
                    </Link>
                  ) : (
                    row.name
                  )}
                </TableCell>
                <TableCell>{row.imprint}</TableCell>
                <TableCell>{row.payment}</TableCell>
                <TableCell>{row.publishing_model}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
